<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="SelectedModal" class="base-modal" tabindex="-1" @click="closeModal" />
    </Transition>

    <div class="custom-modal-wrap">
      <Transition name="slide-down">
        <div v-if="SelectedModal" ref="modalRef" tabindex="-1" role="dialog">
          <slot name="base" />
          <component :is="SelectedModal" />
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<script setup>
import {
  ref,
  shallowRef,
  onMounted,
  onBeforeUnmount,
  nextTick,
  defineAsyncComponent,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import useModalStore from '@global/stores/useModalStore';

import AddonDetailPopup from '@global/components/addon/parts/AddonDetailPopup.vue';
import AdvancedPopup from '@global/components/shop/AdvancedPopup.vue';

const modalStore = useModalStore();
const { closeModal } = modalStore;
const { modalName, isShow } = storeToRefs(modalStore);
const SelectedModal = shallowRef('');
const modals = require.context('@components/modals', true, /\.vue$/);

function getModal(name) {
  const componentPath = `./${name}.vue`;
  if (modals.keys().includes(componentPath)) {
    return modals(componentPath).default;
  }
  return null;
}

watch(isShow, (nV) => {
  if (!nV) {
    close();
  }
});

function close() {
  if (document.getElementById('lb-order-loading') !== null) return;
  SelectedModal.value = '';
}

const modalRef = ref(null);
function trapFocus(event) {
  const modal = modalRef.value;
  if (!modal) return;

  const focusableElements = modal.querySelectorAll(
    'a[href]:not(.disabled), button:not(.disabled), input, select, textarea, [tabindex]:not([tabindex="-1"])',
  );
  const firstFocusableElement = focusableElements[0];
  const lastFocusableElement = focusableElements[focusableElements.length - 1];

  if (event.key === 'Tab') {
    if (event.shiftKey) {
      // Shift + Tab 反向循環
      if (document.activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
      }
    } else if (document.activeElement === lastFocusableElement) {
      // Tab 正向循環
      event.preventDefault();
      firstFocusableElement.focus();
    }
  }
}

onMounted(async () => {
  // 優先載入src component，如果沒有就載入f2e
  const specialModal = {
    AddonDetailPopup,
    AdvancedPopup,
  };
  const selectedComponentLoader = async () => {
    const component = getModal(modalName.value);
    if (component) return component;

    return specialModal[modalName.value];
  };
  SelectedModal.value = defineAsyncComponent(selectedComponentLoader);

  // 跳窗開啟後，按tab鍵只能focus在跳窗
  await nextTick();
  const modal = modalRef.value;
  if (modal) {
    modal.focus();
  }
  window.addEventListener('keydown', trapFocus);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', trapFocus);
});
</script>
